import { NarrativeCardPopulated } from '@/components/NarrativeCard';
import { memo, useCallback } from 'react';
import { useTracking } from 'react-tracking';
import type { Narrative } from '../hooks/useNarrativesCommunityInfinite';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import { useNarrativeCommunityQueryParamCursors } from '../hooks/useNarrativeCommunityQueryParamCursors';

type Props = {
  narrative: Narrative;
  hasPreviousPage: boolean;
  after: string | undefined;
  before: string | undefined;
  updateNarrativeVisibility: ReturnType<
    typeof useNarrativeCommunityQueryParamCursors
  >['updateNarrativeVisibility'];
};

const NarrativeCommunityListItemBase = ({
  narrative,
  hasPreviousPage,
  after,
  before,
  updateNarrativeVisibility,
}: Props) => {
  const { trackEvent } = useTracking();
  const onCardClick = useCallback(() => {
    trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: narrative.id,
      companyId: narrative.company?.id,
      valuationType: 'USER_DEFINED',
    });
  }, [trackEvent, narrative]);

  const onIntersectionChange = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!hasPreviousPage)
        return updateNarrativeVisibility({
          narrativeId: narrative.id,
          isVisible: entry.isIntersecting,
          after: null,
          before: null,
        });

      updateNarrativeVisibility({
        narrativeId: narrative.id,
        isVisible: entry.isIntersecting,
        after,
        before,
      });
    },
    [hasPreviousPage, narrative.id, updateNarrativeVisibility, after, before]
  );

  return (
    <IntersectionObserver threshold={1} onChange={onIntersectionChange}>
      <div role="listitem">
        <NarrativeCardPopulated
          showCompanyIcon
          narrative={narrative}
          onClick={onCardClick}
        />
      </div>
    </IntersectionObserver>
  );
};

export const NarrativeCommunityListItem = memo(NarrativeCommunityListItemBase);
